import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { UnifiedCitation, DocumentViewRequest } from '../types/citation';
import { toDocumentViewRequest } from '../types/citation';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface DocumentViewerContextType {
  /** Currently open document (null if nothing is open). */
  activeDocument: DocumentViewRequest | null;
  /** Open a document for viewing in the sidebar panel. */
  openDocument: (request: DocumentViewRequest) => void;
  /** Close the currently open document. */
  closeDocument: () => void;
}

// ─── Context ────────────────────────────────────────────────────────────────────

const DocumentViewerContext = createContext<DocumentViewerContextType | undefined>(undefined);

// ─── Hook: useDocumentViewer ────────────────────────────────────────────────────

export const useDocumentViewer = (): DocumentViewerContextType => {
  const context = useContext(DocumentViewerContext);
  if (context === undefined) {
    throw new Error('useDocumentViewer must be used within a DocumentViewerProvider');
  }
  return context;
};

// ─── Hook: useDocumentNavigation ────────────────────────────────────────────────
// Convenience hook for citation components. Takes a UnifiedCitation and opens
// the document in the sidebar panel.

export const useDocumentNavigation = () => {
  const { openDocument } = useDocumentViewer();

  return useCallback(
    (citation: UnifiedCitation) => {
      if (!citation.documentId) return;
      openDocument(toDocumentViewRequest(citation));
    },
    [openDocument],
  );
};

// ─── Provider ───────────────────────────────────────────────────────────────────

interface DocumentViewerProviderProps {
  children: ReactNode;
}

export const DocumentViewerProvider: React.FC<DocumentViewerProviderProps> = ({ children }) => {
  const [activeDocument, setActiveDocument] = useState<DocumentViewRequest | null>(null);

  const openDocument = useCallback((request: DocumentViewRequest) => {
    setActiveDocument(request);
  }, []);

  const closeDocument = useCallback(() => {
    setActiveDocument(null);
  }, []);

  return (
    <DocumentViewerContext.Provider
      value={{ activeDocument, openDocument, closeDocument }}
    >
      {children}
    </DocumentViewerContext.Provider>
  );
};
